import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import ChartCardContainer from "./ChartCardContainer";

export default function Co2Calculator({
  title = "Co2 Calculator",
  months = ["dec", "feb", "apr", "jun", "aug", "okt", "nov"],
  // Provide a default fluctuating dataset within 0–90,000
  data = [12000, 38000, 26000, 52000, 41000, 73000, 68000],
  height = 280,
  // Number of additional points BETWEEN each month (must be >= 3 to satisfy requirement)
  pointsBetween = 3,
  // Controls how jagged the line between months should be (0..1+)
  roughness = 0.6,
}) {
  const [transportOn, setTransportOn] = useState(true);
  const [priceOn, setPriceOn] = useState(false);

  // Build upsampled data points with significant randomness between months
  const { upsampledData, categoriesIdx, monthLabelPositions } = useMemo(() => {
    const clampY = (v) => Math.max(0, Math.min(90000, v));
    const k = Math.max(3, Math.floor(pointsBetween)); // at least 3 points BETWEEN months
    const up = [];
    const cats = [];
    const positions = new Set();
    let idx = 0;

    for (let i = 0; i < data.length - 1; i++) {
      const y0 = Number(data[i]);
      const y1 = Number(data[i + 1]);

      // include the exact start-of-month value (kept untouched)
      up.push(clampY(y0));
      cats.push(idx);
      positions.add(idx); // original month label position

      // Compute span and base step to size the jitter
      const span = Math.max(1, Math.abs(y1 - y0));
      // amplitude scales with span and roughness, but has a floor so even small spans look jagged
      const amp = Math.max(2000, span * (0.5 + roughness));

      // create k jittered points strictly between months
      for (let j = 1; j <= k; j++) {
        const t = j / (k + 1); // linear position between months
        const base = y0 + (y1 - y0) * t; // linear baseline
        // randomize around the baseline; include a small high-frequency component
        const jitter = (Math.random() * 2 - 1) * amp;
        const micro = (Math.random() * 2 - 1) * (amp * 0.25);
        const y = clampY(base + jitter + micro);
        up.push(y);
        idx += 1;
        cats.push(idx);
      }

      // advance idx for the next start-of-month point (which will be added at next loop iteration)
      idx += 1;
    }

    // push the final month value and mark its label position (kept untouched)
    up.push(clampY(Number(data[data.length - 1])));
    cats.push(idx);
    positions.add(idx);

    return { upsampledData: up, categoriesIdx: cats, monthLabelPositions: positions };
  }, [data, pointsBetween, roughness]);

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        background: "transparent",
        toolbar: { show: false },
        sparkline: { enabled: false },
        animations: { enabled: true },
        fontFamily: 'NeoSans, sans-serif',
      },
      stroke: {
        curve: "straight",
        width: 2,
        colors: ["#16d07f"], // green to match the toggle
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          shadeIntensity: 0.5,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 100],
          colorStops: [
            {
              offset: 0,
              color: "#16d07f",
              opacity: 0.4,
            },
            {
              offset: 100,
              color: "#16d07f",
              opacity: 0.05,
            },
          ],
        },
      },
      colors: ["#16d07f"],
      dataLabels: { enabled: false },
      markers: { size: 0 },
      grid: {
        show: true,
        borderColor: "rgba(255, 255, 255, 0.1)",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      xaxis: {
        type: "category",
        categories: categoriesIdx,
        labels: {
          style: { colors: categoriesIdx.map(() => "rgba(255, 255, 255, 0.7)") },
          formatter: (val, opts) => {
            const i = Number(val);
            // Show month labels only at original month positions
            if (monthLabelPositions.has(i)) {
              // Determine which month to show by order of occurrence
              // Map index back to month order by counting how many positions <= i
              let order = 0;
              monthLabelPositions.forEach((pos) => {
                if (pos <= i) order += 1;
              });
              // order is count up to current; label index = order - 1
              const labelIdx = Math.max(0, Math.min(months.length - 1, order - 1));
              return String(months[labelIdx]);
            }
            return "";
          },
        },
        axisBorder: { color: "rgba(255, 255, 255, 0.1)" },
        axisTicks: { color: "rgba(255, 255, 255, 0.1)" },
      },
      yaxis: {
        min: 0,
        max: 90000,
        tickAmount: 6,
        labels: {
          style: { colors: "rgba(255, 255, 255, 0.7)" },
          formatter: (val) => {
            const n = Math.round(val);
            return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          },
        },
      },
      legend: { show: false },
      tooltip: {
        theme: "dark",
        x: {
          formatter: (val) => {
            const i = Number(val);
            // Determine nearest month index for display
            // Count how many original positions are strictly less than or equal to i
            const posArr = Array.from(monthLabelPositions).sort((a, b) => a - b);
            let monthIdx = 0;
            for (let p = 0; p < posArr.length; p++) {
              if (i >= posArr[p]) monthIdx = p;
            }
            return months[monthIdx] ?? "";
          },
        },
        y: {
          formatter: (val) => {
            const n = Math.round(val);
            return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: Math.max(220, height - 40) },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: { height: Math.max(200, height - 60) },
            xaxis: { labels: { rotate: 0 } },
          },
        },
      ],
    }),
    [categoriesIdx, monthLabelPositions, months, height]
  );

  const series = useMemo(
    () => [
      {
        name: "CO2 Price",
        data: upsampledData,
      },
    ],
    [upsampledData]
  );

  return (
    <ChartCardContainer
      title="CO2"
      onIconClick={() => console.log('CO2 chart action clicked')}
    >
      {/* Co2 Calculator title */}
      <h3 className="co2-calculator-title">Co2 Calculator</h3>

      {/* Sub-controls: toggles + dropdown buttons */}
      <div className="co2-controls">
        <div className="co2-toggles">
          <Toggle label="Transportation" on={transportOn} onChange={() => setTransportOn(v => !v)} onColor="#16d07f" offColor="#3a3a48" />
          <Toggle label="Price input" on={priceOn} onChange={() => setPriceOn(v => !v)} onColor="#16d07f" offColor="#cc3f3f" />
        </div>
        <div className="co2-dropdown-buttons">
          <button className="chart-btn">
            Source ▾
          </button>
          <button className="chart-btn">
            CO2 Price ▾
          </button>
        </div>
      </div>

      {/* Green-Price Chart subtitle */}
      <h4 className="co2-chart-subtitle">Green-Price Chart</h4>

      {/* Chart */}
      <div className="co2-chart-area">
        <Chart options={options} series={series} type="area" height={height} width="100%" />
      </div>
    </ChartCardContainer>
  );
}

function Toggle({ label, on, onChange, onColor = "#16d07f", offColor = "#cc3f3f" }) {
  const isPriceToggle = label.toLowerCase().includes('price');

  return (
    <div className="co2-toggle">
      <span className="co2-toggle-label">{label}</span>
      <button
        onClick={onChange}
        type="button"
        className={`co2-toggle-switch ${on ? 'on' : 'off'} ${isPriceToggle && !on ? 'price' : ''}`}
      >
        <span className="co2-toggle-knob" />
      </button>
    </div>
  );
}
